import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatCurrency, formatPercentage, formatNumber } from '../../utils/formatters';
import { 
  Wallet, 
  TrendingUp, 
  ArrowUpRight, 
  ArrowDownRight,
  Calendar,
  ChevronRight,
  Sparkles,
  Calculator,
  FileText,
  Download
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import PendingAccountBanner from '../../components/PendingAccountBanner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UserDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulatorData, setSimulatorData] = useState({
    amount: 10000,
    months: 12,
    percentage: user?.profit_percentage || 8.6
  });

  useEffect(() => {
    fetchDashboard();
    fetchEvolution();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvolution = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/evolution`);
      setEvolution(response.data.evolution || []);
    } catch (error) {
      // Error silenced: 'Failed to fetch evolution:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Simulator calculation with compound interest
  const calculateSimulation = () => {
    const { amount, months, percentage } = simulatorData;
    const monthlyRate = percentage / 100;
    const results = [];
    let currentBalance = amount;
    let totalReturns = 0;

    for (let i = 1; i <= months; i++) {
      const monthReturn = currentBalance * monthlyRate;
      totalReturns += monthReturn;
      currentBalance += monthReturn; // Compound interest
      results.push({
        month: i,
        balance: Math.round(currentBalance * 100) / 100,
        returns: Math.round(totalReturns * 100) / 100,
        monthReturn: Math.round(monthReturn * 100) / 100
      });
    }
    return results;
  };

  const simulationResults = calculateSimulation();

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me/statement/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extrato_sgfi_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Error silenced: 'Failed to download PDF:', error);
      // Fallback to text report if PDF fails
      const report = `
SGFI Capital - Extrato de Conta
================================
Cliente: ${user?.name}
Email: ${user?.email}
Data: ${new Date().toLocaleDateString('pt-PT')}

RESUMO DA CONTA
---------------
Saldo Atual: ${formatCurrency(dashboard?.balance)}
Taxa de Rendimento: ${formatPercentage(dashboard?.profit_percentage)}
Rendimentos Acumulados: ${formatCurrency(dashboard?.accumulated_returns)}
Previsão Este Mês: ${formatCurrency(dashboard?.expected_this_month)}

================================
SGFI Capital
      `;
      const blob = new Blob([report], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SGFI_Extrato_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  const progressPercentage = dashboard?.monthly_calculation 
    ? (dashboard.monthly_calculation.current_day / dashboard.monthly_calculation.days_in_month) * 100 
    : (dashboard?.days_invested && dashboard?.days_in_month 
        ? (dashboard.days_invested / dashboard.days_in_month) * 100 
        : 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#252F38] border border-white/10 rounded-xl p-3 shadow-xl">
          <p className="text-slate-400 text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-white font-medium">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="user-dashboard">
      {/* Pending Account Banner */}
      <PendingAccountBanner />

      {/* Welcome Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400 text-sm sm:text-base mt-1">{t('dashboard.welcomeSubtitle')}</p>
        </div>
        
        {/* Action Buttons - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
          <Dialog open={showSimulator} onOpenChange={setShowSimulator}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-white/5 hover:bg-white/10 border-white/10 rounded-xl sm:rounded-full h-12 sm:h-auto sm:px-6 text-sm active:scale-95 transition-transform" data-testid="simulator-btn">
                <Calculator className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.simulator')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#252F38] border-white/10 w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto mx-auto rounded-2xl">
              <DialogHeader>
                <DialogTitle className="text-white font-['Outfit'] flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-blue-400" />
                  {t('dashboard.investmentSimulator')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 sm:space-y-6 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm">{t('dashboard.initialCapital')}</Label>
                    <Input
                      type="number"
                      value={simulatorData.amount}
                      onChange={(e) => setSimulatorData({...simulatorData, amount: parseFloat(e.target.value) || 0})}
                      className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t('dashboard.months')}</Label>
                    <Input
                      type="number"
                      value={simulatorData.months}
                      onChange={(e) => setSimulatorData({...simulatorData, months: parseInt(e.target.value) || 1})}
                      className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t('dashboard.monthlyRatePercent')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={simulatorData.percentage}
                      onChange={(e) => setSimulatorData({...simulatorData, percentage: parseFloat(e.target.value) || 0})}
                      className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl h-12"
                    />
                  </div>
                </div>

                {/* Simulation Results */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <p className="text-slate-400 text-xs sm:text-sm">{t('dashboard.finalBalance')}</p>
                    <p className="text-lg sm:text-2xl font-bold text-emerald-400">
                      {formatCurrency(simulationResults[simulationResults.length - 1]?.balance)}
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                    <p className="text-slate-400 text-xs sm:text-sm">{t('dashboard.totalProfits')}</p>
                    <p className="text-lg sm:text-2xl font-bold text-blue-400">
                      {formatCurrency(simulationResults[simulationResults.length - 1]?.returns)}
                    </p>
                  </div>
                </div>

                {/* Chart */}
                <div className="h-48 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={simulationResults}>
                      <defs>
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickFormatter={(v) => `M${v}`} />
                      <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} width={45} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="balance" name={t('dashboard.balance')} stroke="#3B82F6" fill="url(#colorBalance)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Monthly Breakdown */}
                <div className="max-h-40 sm:max-h-48 overflow-y-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="text-slate-400 border-b border-white/10">
                      <tr>
                        <th className="text-left py-2">{t('dashboard.month')}</th>
                        <th className="text-right py-2">{t('dashboard.profit')}</th>
                        <th className="text-right py-2">{t('dashboard.balance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulationResults.map((row) => (
                        <tr key={row.month} className="border-b border-white/5">
                          <td className="py-2 text-slate-300">{t('dashboard.month')} {row.month}</td>
                          <td className="py-2 text-right text-emerald-400">+{formatCurrency(row.monthReturn)}</td>
                          <td className="py-2 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  * {t('dashboard.compoundInterestNote') || 'Simulação com juros compostos (reinvestimento automático)'}
                </p>
              </div>
            </DialogContent>
          </Dialog>

          <Button onClick={handleDownloadPDF} variant="outline" className="bg-white/5 hover:bg-white/10 border-white/10 rounded-xl sm:rounded-full h-12 sm:h-auto sm:px-6 text-sm active:scale-95 transition-transform" data-testid="download-pdf-btn">
            <Download className="w-5 h-5 sm:mr-2" />
            <span className="hidden sm:inline">{t('dashboard.statement')}</span>
          </Button>

          <Link
            to="/deposit"
            className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl sm:rounded-full h-12 sm:h-auto sm:px-6 sm:py-3 font-semibold shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all active:scale-95"
            data-testid="deposit-btn"
          >
            <ArrowDownRight className="w-5 h-5" />
            <span className="text-sm sm:text-base">{t('dashboard.makeDeposit')}</span>
          </Link>
          <Link
            to="/withdraw"
            className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 active:bg-white/15 text-white border border-white/10 rounded-xl sm:rounded-full h-12 sm:h-auto sm:px-6 sm:py-3 font-medium transition-all active:scale-95"
            data-testid="withdraw-btn"
          >
            <ArrowUpRight className="w-5 h-5" />
            <span className="text-sm sm:text-base">{t('dashboard.requestWithdrawal')}</span>
          </Link>
        </div>
      </div>

      {/* Pending Month Alert - if previous month wasn't paid */}
      {dashboard?.pending_month_return && (
        <div className="glass-card p-4 sm:p-6 mb-4 border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5" data-testid="pending-month-alert">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider">{t('dashboard.pendingOf')} {dashboard.pending_month_return.month_name}</span>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-amber-400 font-['Outfit']">
                {formatCurrency(dashboard.pending_month_return.amount)}
              </div>
              <p className="text-slate-400 text-xs sm:text-sm mt-1">
                Rendimento de {dashboard.pending_month_return.month_name} ainda não processado. Aguarde o administrador.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* =============== MAIN BALANCE CARD - BANK STYLE =============== */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a2942] via-[#162236] to-[#0d1829] border border-white/10 p-6 sm:p-8" data-testid="main-balance-card">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative">
          {/* Label */}
          <div className="flex items-center gap-2 text-slate-400 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm font-medium uppercase tracking-wider">{t('dashboard.totalBalance')}</span>
          </div>
          
          {/* Main Balance */}
          <div className="mb-6">
            <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-white font-['Outfit'] tracking-tight">
              {formatCurrency(dashboard?.balance)}
            </div>
            <p className="text-slate-500 text-sm mt-2">{t('dashboard.availableCapital')}</p>
          </div>
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{t('dashboard.monthlyRate')}</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-emerald-400">{formatPercentage(dashboard?.profit_percentage)}</span>
              </div>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{t('dashboard.totalReceived')}</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">{formatCurrency(dashboard?.accumulated_returns)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =============== MONTHLY EARNINGS CARD =============== */}
      <div className="glass-card p-5 sm:p-6 border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-transparent" data-testid="monthly-earnings-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-emerald-400">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium uppercase tracking-wider">
                {dashboard?.pending_month_return 
                  ? `${t('dashboard.expectedThisMonth')} - ${dashboard.monthly_calculation?.month_name || ''}`
                  : t('dashboard.expectedThisMonth')
                }
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-emerald-400 font-['Outfit']">
              +{formatCurrency(dashboard?.expected_this_month)}
            </div>
          </div>
          <Link
            to="/rendimentos"
            className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors text-sm"
          >
            <span>{t('common.viewDetails')}</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        {/* Calculation Breakdown - Collapsible Summary */}
        {dashboard?.monthly_calculation && (
          <div className="mt-4 pt-4 border-t border-white/10">
            {/* Compact Summary Row */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-slate-500 text-xs mb-1">{t('dashboard.baseBalance')}</p>
                <p className="text-white text-sm font-medium">{formatCurrency(dashboard.monthly_calculation.balance_at_start)}</p>
              </div>
              {dashboard.monthly_calculation.deposits_this_month > 0 && (
                <div>
                  <p className="text-blue-400/70 text-xs mb-1">+ Depósitos</p>
                  <p className="text-blue-400 text-sm font-medium">{formatCurrency(dashboard.monthly_calculation.deposits_this_month)}</p>
                </div>
              )}
              {dashboard.monthly_calculation.withdrawals_this_month > 0 && (
                <div>
                  <p className="text-red-400/70 text-xs mb-1">- Levantamentos</p>
                  <p className="text-red-400 text-sm font-medium">{formatCurrency(dashboard.monthly_calculation.withdrawals_this_month)}</p>
                </div>
              )}
              <div>
                <p className="text-slate-500 text-xs mb-1">{t('dashboard.dayOfMonth')}</p>
                <p className="text-white text-sm font-medium">{dashboard.monthly_calculation.current_day}/{dashboard.monthly_calculation.days_in_month}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =============== SECONDARY STATS GRID =============== */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {/* Days Progress This Month */}
        <div className="glass-card p-4 sm:p-5" data-testid="progress-card">
          <div className="flex items-center gap-2 text-slate-400 mb-3">
            <Calendar className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-medium uppercase tracking-wider">{t('dashboard.monthProgress')}</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">{t('dashboard.day')} {dashboard?.monthly_calculation?.current_day || dashboard?.days_invested}</span>
              <span className="text-white font-medium">
                {Math.round(progressPercentage)}%
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2 bg-purple-900/30" />
          </div>
        </div>

        {/* Account Status */}
        <div className="glass-card p-4 sm:p-5" data-testid="status-card">
          <div className="flex items-center gap-2 text-slate-400 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium uppercase tracking-wider">{t('dashboard.status')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-lg font-semibold text-emerald-400">{t('dashboard.activeAccount')}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{t('dashboard.calculatingReturns')}</p>
        </div>
      </div>

      {/* Evolution Chart */}
      {evolution.length > 1 && (
        <div className="glass-card p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white font-['Outfit'] mb-4 sm:mb-6">{t('dashboard.investmentEvolution')}</h2>
          <div className="h-48 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="month" 
                  stroke="#64748B" 
                  fontSize={12}
                  tickFormatter={(v) => {
                    const [year, month] = v.split('-');
                    return `${month}/${year.slice(2)}`;
                  }}
                />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="balance" name={t('dashboard.balance')} stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
                <Line type="monotone" dataKey="returns" name={t('nav.rendimentos')} stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent Payments */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white font-['Outfit']">{t('dashboard.recentPayments')}</h2>
          <Link 
            to="/rendimentos" 
            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 transition-colors"
          >
            {t('common.viewAll')} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        {dashboard?.monthly_payments?.length > 0 ? (
          <div className="space-y-3">
            {dashboard.monthly_payments.slice(0, 5).map((payment, index) => (
              <div 
                key={payment.id || index}
                className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{payment.description}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(payment.created_at).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>
                <span className="text-emerald-400 font-semibold">
                  +{formatCurrency(payment.amount)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('dashboard.noPayments')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
